import React, { useState, useRef } from 'react';
import { useField } from 'formik';

import { numToLetter } from '~/commons/helpers';
import useEventListener from '~/commons/hooks/useEventListener';

const colors = [
  '#59BCCB',
  '#F4F438',
  '#FFA02E',
  '#36AD37',
  '#E54C75',
  '#344DE2',
  '#ED784F',
  '#9E8AFF',
  '#9ECFA1',
  '#FF80FF',
];

const getColor = ({ matching, index }) => {
  if (matching) {
    return {
      color: '#212529',
      border: 'solid 3px #212529',
    };
  }

  if (index !== undefined) {
    return {
      color: colors[index],
      border: `solid 3px ${colors[index]}`,
    };
  }

  return {
    color: '#bbb',
    border: 'solid 3px #eee',
  };
};

const FieldsMatching = ({
  qtnIndex: i,
  question: q,
  isCorrect,
  correctAns: cAns,
  disabled = false,
}) => {
  const fieldRef = useRef();
  const [, { value }, { setValue }] = useField(`answers.${i}.answeredSeq`);
  const [matching, setMatching] = useState(null);
  let vv = [...value];

  const match = (type, index, rIndex) => {
    if (!disabled) {
      if (matching && matching.type !== type) {
        let slot; let ans;

        if (type === 'clues') {
          slot = matching.index;
          ans = index;
        } else {
          slot = index;
          ans = matching.index;
        }

        // remove duplicates
        vv = vv.map((v) => (v === ans ? undefined : v));

        // set value
        vv[slot] = ans;

        setValue(vv);
        setMatching(null);
      } else {
        setMatching({ type, index, rIndex });
      }
    }
  };

  // defocus when clicking outside
  useEventListener(document, 'click', (e) => {
    if (!fieldRef.current.contains(e.target)) {
      setMatching(null);
    }
  }, [fieldRef.current]);

  return (
    <div className="mt-2">
      {/* answer */}
      <div ref={fieldRef}>
        {q.choices.map(({ c, ii }, jj) => {
          // Handle both string and object format for choices
          const choiceText = typeof c === 'string' ? c : c.text;
          const choiceImg = typeof c === 'object' ? c.img : null;

          return (
            <div key={ii} className="mb-2">
              <div className="form-row">
                {/* clues */}
                <div className="col">
                  <div
                    onClick={() => match('clues', ii, jj)}
                    className={`
                      row m-0
                      h-100 cursor-pointer
                    `}
                    style={{
                      ...getColor({
                        matching: matching?.type === 'clues'
                          && matching.index === ii,
                        index: vv.find((v) => parseInt(v, 10) === ii) !== undefined
                          ? ii : undefined,
                      }),
                      borderRadius: '0px !important',
                    }}
                  >
                    <div className="col-auto p-1">
                      {`(${Number(jj) + 1})`}.
                    </div>
                    <div className="col p-1">
                      {choiceText}
                      {choiceImg && (
                        <div className="mt-1">
                          <img
                            src={choiceImg}
                            alt={choiceText || `Choice ${jj + 1}`}
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              display: 'block',
                            }}
                            className="img-fluid"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* matches */}
                <div className="col">
                  {(() => {
                    const matchOffset = q.choices.length;
                    const matchIdx = q.randoms[jj + matchOffset];
                    const matchObj = q.matches && Array.isArray(q.matches) && matchIdx < q.matches.length ? q.matches[matchIdx] : null;
                    const matchText = typeof matchObj === 'object' && matchObj !== null ? matchObj.text : (typeof matchObj === 'string' ? matchObj : '');
                    const matchImg = typeof matchObj === 'object' && matchObj !== null ? matchObj.img : '';
                    return (
                      <div
                        onClick={() => match('matches', matchIdx, jj)}
                        className={`
                          row m-0
                          h-100 cursor-pointer
                        `}
                        style={{
                          ...getColor({
                            matching: matching?.type === 'matches'
                              && matching.index === matchIdx,
                            index: vv[matchIdx],
                          }),
                          borderRadius: '0px !important',
                        }}
                      >
                        <div className="col-auto p-1">
                          {numToLetter(jj, true)}.
                        </div>
                        <div className="col p-1">
                          {matchText}
                          {matchImg && (
                            <div className="mt-1">
                              <img
                                src={matchImg}
                                alt={matchText || `Match ${jj + 1}`}
                                style={{
                                  maxWidth: '100%',
                                  height: 'auto',
                                  display: 'block',
                                }}
                                className="img-fluid"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* answered */}
      {value && !value.includes(undefined) && (
        <div className={`
          mt-3 py-2 px-4 position-relative
          ${
            cAns !== undefined && !isCorrect
              ? 'bg-light' : 'bg-quinary'
          }
        `}>
          我的答案:
          &nbsp;
          {(() => {
            const matchOffset = q.choices.length;
            return value
              .map((a, k) => {
                if (a === undefined) return '';

                const choice = q.choices.find(({ ii }) => parseInt(a, 10) === ii);
                if (!choice) {
                  return '';
                }

                const rValue = choice.r !== undefined ? choice.r : a;
                const letterIndex = q.randoms.slice(matchOffset).indexOf(k);
                return `[(${(Number(rValue + 1))}),${numToLetter(letterIndex, true)}]`;
              })
              .filter((item) => item !== '') // Remove empty items
              .sort((a, b) => {
                const numA = parseInt(a.match(/\d+/)[0], 10);
                const numB = parseInt(b.match(/\d+/)[0], 10);
                return numA - numB;
              })
              .join(' ');
          })()};
          {/* correct/incorrect mark */}
          {cAns !== undefined && (
            <img
              className="pos-abs-br mr-3 mb-2"
              {...isCorrect ? {
                src: '/assets/img/correct.svg',
                style: { width: '42px', height: '36px' },
              } : {
                src: '/assets/img/incorrect.svg',
                style: { width: '34px', height: '40px' },
              }}
            />
          )}
        </div>
      )}
      {/* correct answer */}
      {(cAns !== undefined && !isCorrect) && (
        <div className="text-tertiary mt-3 py-2 px-4">
          正確答案:
          &nbsp;
          {(() => {
            const matchOffset = q.choices.length;
            return cAns
              .map((a, k) => {
                if (a === undefined) return '';

                const choice = q.choices.find(({ ii }) => parseInt(a, 10) === ii);
                if (!choice) {
                  return '';
                }

                const rValue = choice.r !== undefined ? choice.r : a;
                const letterIndex = q.randoms.slice(matchOffset).indexOf(k);
                const result = `[(${Number(rValue + 1)}),${numToLetter(letterIndex, true)}]`;

                return result;
              })
              .filter((item) => item !== '') // Remove empty items
              .sort((a, b) => {
                const numA = parseInt(a.match(/\d+/)[0], 10);
                const numB = parseInt(b.match(/\d+/)[0], 10);
                return numA - numB;
              })
              .join(' ');
          })()};
        </div>
      )}
    </div>
  );
};

export default FieldsMatching;
